import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  StandardizedTable,
  TrendIndicator as StdTrendIndicator,
  type TableRow,
} from "@/components/ui/standardized-table";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { TrendingUp, TrendingDown } from "lucide-react";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatSignedPercent } from "@/utils/numberFormat";
import { FilterState, formatDateToISO } from "@/features/filters/filter.types";

import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import {
  CarregandoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import { useAccount } from "@/contexts/account.context";
import { formatDateBR } from "@/utils/dateFormat";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import type { TableQueryDTO } from "@/types/table-query";
import {
  apiV1AcquisitionAudienceQualityGet,
  getAcquisitionCampaignsApiV1AcquisitionCampaignsGet,
  getAcquisitionChannelGroupingApiV1AcquisitionChannelGroupingGet,
  getAcquisitionGeoUsersNewByRegionApiV1AcquisitionGeoUsersNewByRegionGet,
  getAcquisitionGeoUsersNewByRegionSourceApiV1AcquisitionGeoUsersNewByRegionSourceGet,
  getAcquisitionGeoUsersReturningByRegionApiV1AcquisitionGeoUsersReturningByRegionGet,
  getAcquisitionGeoUsersReturningByRegionSourceApiV1AcquisitionGeoUsersReturningByRegionSourceGet,
  getAcquisitionKpisApiV1AcquisitionKpisGet,
  getAcquisitionNewVsReturningApiV1AcquisitionNewVsReturningGet,
  getAcquisitionSourceMediumApiV1AcquisitionSourceMediumGet,
  getAcquisitionTimelineApiV1AcquisitionTimelineGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  AcquisitionChannelGroupingItem as GeneratedAcquisitionChannelGroupingItem,
  AcquisitionGeoUsersItem as GeneratedAcquisitionGeoUsersItem,
  AcquisitionKpis as GeneratedAcquisitionKpis,
  AcquisitionTableItem as GeneratedAcquisitionTableItem,
  AcquisitionTimelinePoint as GeneratedAcquisitionTimelinePoint,
  GetAcquisitionCampaignsApiV1AcquisitionCampaignsGetData,
  GetAcquisitionKpisApiV1AcquisitionKpisGetData,
  GetAcquisitionTimelineApiV1AcquisitionTimelineGetData,
} from "@/backend-sdk/client/types.gen";
import { KpiNumber } from "@/components/ui/kpi-number";
import {
  DrilldownProvider,
  useDrilldownOptional,
} from "@/features/drilldown/drilldown.context";
import type { DrillDimension } from "@/features/drilldown/drilldown.context";
import { ActiveFiltersDisplay } from "@/features/drilldown/ActiveFiltersDisplay";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";

type AcquisitionKpis = GeneratedAcquisitionKpis;
type AcquisitionTimelinePoint = GeneratedAcquisitionTimelinePoint;
type AcquisitionChannelGroupingItem = GeneratedAcquisitionChannelGroupingItem &
  TableRow;
type AcquisitionTableItem = GeneratedAcquisitionTableItem & TableRow;
type AcquisitionGeoUsersItem = GeneratedAcquisitionGeoUsersItem & TableRow;
type TrendDirection = "up" | "down" | "neutral";
type AcquisitionCrossFilterParams = Partial<
  Pick<
    GetAcquisitionTimelineApiV1AcquisitionTimelineGetData["query"],
    | "filter_channel_group"
    | "filter_source_medium"
    | "filter_campaign"
    | "filter_region"
    | "filter_city"
    | "filter_category"
    | "filter_product"
    | "filter_device"
    | "filter_age"
    | "filter_gender"
    | "filter_new_returning"
  >
>;
type AcquisitionCrossFilterKey = keyof AcquisitionCrossFilterParams;
type AcquisitionTableQueryParams = Pick<
  GetAcquisitionCampaignsApiV1AcquisitionCampaignsGetData["query"],
  "q" | "sort_by" | "sort_dir" | "filters"
>;
type AcquisitionBaseQuery = Pick<
  GetAcquisitionKpisApiV1AcquisitionKpisGetData["query"],
  "customer_uuid" | "start_date" | "end_date" | "comparison" | "account_id"
>;

interface TooltipPayloadEntry {
  dataKey?: string | number;
  value?: string | number;
  color?: string;
}

interface LineLabelProps {
  x?: string | number;
  y?: string | number;
  value?: string | number;
}

const toTrendDirection = (value: string | null | undefined): TrendDirection => {
  if (value === "up" || value === "down" || value === "neutral") {
    return value;
  }
  return "neutral";
};

const toTableRows = <T extends object>(items: T[]): (T & TableRow)[] =>
  items.map((item) => ({ ...item }) as T & TableRow);

const getTableQueryParams = (
  tableQuery: TableQueryDTO,
): AcquisitionTableQueryParams => ({
  q: tableQuery.q,
  sort_by: tableQuery.sortBy,
  sort_dir: tableQuery.sortDir,
  filters: tableQuery.filters?.length
    ? JSON.stringify(tableQuery.filters)
    : undefined,
});

// Funções de formatação
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
};

const formatNumber = (value: number) => {
  const num = Number(value);
  if (value === null || value === undefined || Number.isNaN(num)) {
    return "N/D";
  }
  return new Intl.NumberFormat("pt-BR").format(num);
};

const formatPercentage = (value: number) => {
  return `${Number(value).toFixed(2)}%`;
};

/** Os oito cards saem do GA4: `/acquisition/kpis` não publica investimento nem ROAS. */
const FONTES_DOS_CARDS: readonly RequisitoDeFonte[] = ["ga4"];

// Componente para exibir indicador de tendência nos KPI cards (layout com ícone à direita)
const TrendIndicator = ({
  value,
  trend,
}: {
  value: number;
  trend: "up" | "down" | "neutral";
}) => {
  const { filter } = useGlobalFilter();
  if (!isComparisonEnabled(filter)) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  if (value === null || value === undefined || isNaN(Number(value))) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  if (trend === "neutral") {
    return <span className="text-muted-foreground text-xs">0,00%</span>;
  }
  return (
    <div className="flex items-center justify-end">
      <span
        className={`text-xs font-semibold mr-2 ${trend === "up" ? "text-success-soft" : "text-error-soft"}`}
      >
        {formatSignedPercent(value)}
      </span>
      {trend === "up" ? (
        <TrendingUp className="h-3.5 w-3.5 text-success-soft" />
      ) : (
        <TrendingDown className="h-3.5 w-3.5 text-error-soft" />
      )}
    </div>
  );
};

// Wrapper para colunas delta nas tabelas — usa o TrendIndicator padronizado (sem negrito)
const renderTableDelta = (value: unknown): React.ReactNode => {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num))
    return <span className="text-muted-foreground">—</span>;
  return <StdTrendIndicator value={num} />;
};

// Função para determinar a tendência baseada no valor
const getTrend = (value: number): "up" | "down" | "neutral" => {
  if (value > 0) return "up";
  if (value < 0) return "down";
  return "neutral";
};

const AcquisitionPage: React.FC = () => {
  const hierarchies = useMemo(
    (): DrillDimension[][] => [
      ["channel_group", "source_medium"],
      ["campaign"],
      ["region"],
      ["new_returning"],
    ],
    [],
  );

  return (
    <DrilldownProvider hierarchies={hierarchies}>
      <AcquisitionPageContent />
    </DrilldownProvider>
  );
};

const AcquisitionPageContent: React.FC = () => {
  const drilldown = useDrilldownOptional();
  const { filter: filterState } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filterState);
  const { selectedAccount } = useAccount();
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const startDate = useMemo(
    () => formatDateToISO(filterState.dateRange.startDate),
    [filterState.dateRange.startDate],
  );
  const endDate = useMemo(
    () => formatDateToISO(filterState.dateRange.endDate),
    [filterState.dateRange.endDate],
  );
  const comparison = useMemo(
    () =>
      filterState.comparison?.type === "previousYear"
        ? "previous_year"
        : "previous_period",
    [filterState.comparison?.type],
  );
  const resolveCustomerUuid = useCallback(() => {
    if (selectedAccount?.customer_uuid) {
      return selectedAccount.customer_uuid;
    }
    if (selectedAccount?.id) {
      return selectedAccount.id;
    }
    if (typeof window !== "undefined") {
      return localStorage.getItem("active_customer_uuid") || undefined;
    }
    return undefined;
  }, [selectedAccount]);
  const resolveAccountId = useCallback(() => {
    if (selectedAccount?.account_id) {
      return selectedAccount.account_id;
    }
    return undefined;
  }, [selectedAccount]);
  const customerUuid = resolveCustomerUuid() ?? null;
  const accountId = resolveAccountId();
  const getBaseQuery = useCallback(
    (resolvedCustomerUuid: string): AcquisitionBaseQuery => ({
      customer_uuid: resolvedCustomerUuid,
      start_date: startDate,
      end_date: endDate,
      comparison,
      account_id: accountId,
    }),
    [accountId, comparison, endDate, startDate],
  );
  const [chartOptions, setChartOptions] = useState({
    sessoes: true,
    taxaConversao: true,
    investimentoGoogle: true,
    investimentoMeta: true,
    roas: true,
  });

  // Começa carregando: o primeiro desenho acontece antes de o efeito ligar a flag, e os oito
  // cards piscavam N/D antes de a busca sair.
  const [loadingKpis, setLoadingKpis] = useState<boolean>(true);
  const [erroKpis, setErroKpis] = useState<boolean>(false);
  const [kpis, setKpis] = useState<AcquisitionKpis | null>(null);
  const [timeline, setTimeline] = useState<AcquisitionTimelinePoint[]>([]);

  // Estados para paginação server-side e dados das quatro tabelas
  const [sourcesPage, setSourcesPage] = useState<number>(1);
  const [campaignsPage, setCampaignsPage] = useState<number>(1);
  const [nvrPage, setNvrPage] = useState<number>(1);
  const [channelGroupingPage, setChannelGroupingPage] = useState<number>(1);
  const pageSize = 10;

  // Query server-side por tabela (busca global / filtros / ordenação)
  const [channelGroupingQuery, setChannelGroupingQuery] =
    useState<TableQueryDTO>({});
  const [sourcesQuery, setSourcesQuery] = useState<TableQueryDTO>({});
  const [campaignsQuery, setCampaignsQuery] = useState<TableQueryDTO>({});
  const [nvrQuery, setNvrQuery] = useState<TableQueryDTO>({});
  const [newByRegionQuery, setNewByRegionQuery] = useState<TableQueryDTO>({});
  const [retByRegionQuery, setRetByRegionQuery] = useState<TableQueryDTO>({});
  const [newByRegionSourceQuery, setNewByRegionSourceQuery] =
    useState<TableQueryDTO>({});
  const [retByRegionSourceQuery, setRetByRegionSourceQuery] =
    useState<TableQueryDTO>({});

  // Uma flag por tabela, e começando ligada pelo mesmo motivo dos KPIs: sem ela, o
  // primeiro desenho dizia "Nenhum dado encontrado" antes de a busca sair.
  const [channelGroupingLoading, setChannelGroupingLoading] =
    useState<boolean>(true);
  const [sourcesLoading, setSourcesLoading] = useState<boolean>(true);
  const [campaignsLoading, setCampaignsLoading] = useState<boolean>(true);
  const [agesLoading, setAgesLoading] = useState<boolean>(true);
  const [nvrLoading, setNvrLoading] = useState<boolean>(true);
  const [newByRegionLoading, setNewByRegionLoading] = useState<boolean>(true);
  const [retByRegionLoading, setRetByRegionLoading] = useState<boolean>(true);
  const [newByRegionSourceLoading, setNewByRegionSourceLoading] =
    useState<boolean>(true);
  const [retByRegionSourceLoading, setRetByRegionSourceLoading] =
    useState<boolean>(true);
  const [channelGroupingData, setChannelGroupingData] = useState<
    AcquisitionChannelGroupingItem[]
  >([]);
  const [channelGroupingTotal, setChannelGroupingTotal] =
    useState<AcquisitionChannelGroupingItem | null>(null);
  const [channelGroupingTotalCount, setChannelGroupingTotalCount] =
    useState<number>(0);

  const [sourcesData, setSourcesData] = useState<AcquisitionTableItem[]>([]);
  const [sourcesTotal, setSourcesTotal] = useState<number>(0);
  const [sourcesTotalRow, setSourcesTotalRow] =
    useState<AcquisitionTableItem | null>(null);

  const [campaignsData, setCampaignsData] = useState<AcquisitionTableItem[]>(
    [],
  );
  const [campaignsTotal, setCampaignsTotal] = useState<number>(0);
  const [campaignsTotalRow, setCampaignsTotalRow] =
    useState<AcquisitionTableItem | null>(null);

  const [nvrData, setNvrData] = useState<AcquisitionTableItem[]>([]);
  const [nvrTotal, setNvrTotal] = useState<number>(0);
  const [nvrTotalRow, setNvrTotalRow] = useState<AcquisitionTableItem | null>(
    null,
  );

  // <auditoria 27> A tabela FAIXA ETÁRIA — a "Qualidade do Público" do Looker. Ela é a
  // única das nove desta página que **não** é clicável: `/acquisition/audience-quality`
  // recorta pelos cross-filters ativos, mas as quatro tabelas de geo não aceitam
  // `filter_age`, e um clique que recortasse cinco tabelas e deixasse quatro intactas
  // seria pior do que não recortar nenhuma.
  const [agesPage, setAgesPage] = useState<number>(1);
  const [agesQuery, setAgesQuery] = useState<TableQueryDTO>({});
  const [agesData, setAgesData] = useState<AcquisitionTableItem[]>([]);
  const [agesTotal, setAgesTotal] = useState<number>(0);
  const [agesTotalRow, setAgesTotalRow] = useState<AcquisitionTableItem | null>(
    null,
  );

  // Estados para as 4 novas tabelas (Geo Users)
  const [newByRegionPage, setNewByRegionPage] = useState<number>(1);
  const [retByRegionPage, setRetByRegionPage] = useState<number>(1);
  const [newByRegionSourcePage, setNewByRegionSourcePage] = useState<number>(1);
  const [retByRegionSourcePage, setRetByRegionSourcePage] = useState<number>(1);

  const [newByRegionData, setNewByRegionData] = useState<
    AcquisitionGeoUsersItem[]
  >([]);
  const [newByRegionTotal, setNewByRegionTotal] = useState<number>(0);
  const [newByRegionTotalRow, setNewByRegionTotalRow] =
    useState<AcquisitionGeoUsersItem | null>(null);
  const [retByRegionData, setRetByRegionData] = useState<
    AcquisitionGeoUsersItem[]
  >([]);
  const [retByRegionTotal, setRetByRegionTotal] = useState<number>(0);
  const [retByRegionTotalRow, setRetByRegionTotalRow] =
    useState<AcquisitionGeoUsersItem | null>(null);
  const [newByRegionSourceData, setNewByRegionSourceData] = useState<
    AcquisitionGeoUsersItem[]
  >([]);
  const [newByRegionSourceTotal, setNewByRegionSourceTotal] =
    useState<number>(0);
  const [newByRegionSourceTotalRow, setNewByRegionSourceTotalRow] =
    useState<AcquisitionGeoUsersItem | null>(null);
  const [retByRegionSourceData, setRetByRegionSourceData] = useState<
    AcquisitionGeoUsersItem[]
  >([]);
  const [retByRegionSourceTotal, setRetByRegionSourceTotal] =
    useState<number>(0);
  const [retByRegionSourceTotalRow, setRetByRegionSourceTotalRow] =
    useState<AcquisitionGeoUsersItem | null>(null);

  const handleFilterChange = (filter: FilterState) => {
    console.log("Filtro aplicado:", filter);
  };

  const handleChartOptionChange = (option: keyof typeof chartOptions) => {
    setChartOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  // extraParams derivados dos filtros de cross-filter
  const crossFilterParams = useMemo(
    (): AcquisitionCrossFilterParams => drilldown?.apiFilters || {},
    [drilldown?.apiFilters],
  );

  // Gera extraParams excluindo o filtro da própria dimensão da tabela
  const getExtraParamsExcluding = useCallback(
    (excludeFilterKey: AcquisitionCrossFilterKey) => {
      const params = { ...crossFilterParams };
      delete params[excludeFilterKey];
      return params;
    },
    [crossFilterParams],
  );

  // Resetar paginações quando o filtro global mudar (evita páginas inválidas)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setChannelGroupingPage(1);
    setSourcesPage(1);
    setCampaignsPage(1);
    setNvrPage(1);
    setAgesPage(1);
    setNewByRegionPage(1);
    setRetByRegionPage(1);
    setNewByRegionSourcePage(1);
    setRetByRegionSourcePage(1);
  }, [
    startDate,
    endDate,
    comparison,
    resolveCustomerUuid,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    // Carregar os KPIs específicos da página de Aquisição
    const loadKpis = async () => {
      try {
        setLoadingKpis(true);
        setErroKpis(false);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setKpis(null);
          setLoadingKpis(false);
          return;
        }
        const { data: kpisData } =
          await getAcquisitionKpisApiV1AcquisitionKpisGet({
            query: {
              ...getBaseQuery(customerUuid),
              ...crossFilterParams,
            },
            throwOnError: true,
          });
        setKpis(kpisData?.kpis || null);
      } catch (e) {
        console.error("Erro ao carregar KPIs (acquisition/kpis):", e);
        setKpis(null);
        setErroKpis(true);
      } finally {
        setLoadingKpis(false);
      }
    };
    loadKpis();
  }, [
    startDate,
    endDate,
    comparison,
    resolveCustomerUuid,
    getBaseQuery,
    crossFilterParams,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  // Novo useEffect para carregar dados do Channel Grouping
  useEffect(() => {
    const loadChannelGrouping = async () => {
      try {
        setChannelGroupingLoading(true);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setChannelGroupingData([]);
          setChannelGroupingTotal(null);
          setChannelGroupingLoading(false);
          return;
        }
        const { data } =
          await getAcquisitionChannelGroupingApiV1AcquisitionChannelGroupingGet(
            {
              query: {
                ...getBaseQuery(customerUuid),
                property_id: accountId,
                page: channelGroupingPage,
                page_size: pageSize,
                ...getTableQueryParams(channelGroupingQuery),
                ...getExtraParamsExcluding("filter_channel_group"),
              },
              throwOnError: true,
            },
          );
        setChannelGroupingData(toTableRows(data?.items || []));
        setChannelGroupingTotal(data?.total_row || null);
        setChannelGroupingTotalCount(
          data?.pagination?.total ?? (data?.items?.length || 0),
        );
      } catch (e) {
        console.error("Erro ao carregar Channel Grouping:", e);
        setChannelGroupingData([]);
        setChannelGroupingTotal(null);
        setChannelGroupingTotalCount(0);
      } finally {
        setChannelGroupingLoading(false);
      }
    };
    loadChannelGrouping();
  }, [
    startDate,
    endDate,
    comparison,
    resolveCustomerUuid,
    getBaseQuery,
    channelGroupingPage,
    channelGroupingQuery,
    accountId,
    getExtraParamsExcluding,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    const loadTimeline = async () => {
      try {
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setTimeline([]);
          return;
        }
        const { data: timelineData } =
          await getAcquisitionTimelineApiV1AcquisitionTimelineGet({
            query: {
              ...getBaseQuery(customerUuid),
              item_name_operator: "contains",
              ...crossFilterParams,
            },
            throwOnError: true,
          });
        setTimeline(
          Array.isArray(timelineData?.series) ? timelineData.series : [],
        );
      } catch (err) {
        console.error("Erro ao carregar timeline (acquisition/timeline):", err);
        setTimeline([]);
      }
    };

    loadTimeline();
  }, [
    startDate,
    endDate,
    comparison,
    resolveCustomerUuid,
    getBaseQuery,
    crossFilterParams,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    const loadSources = async () => {
      try {
        setSourcesLoading(true);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setSourcesData([]);
          setSourcesTotal(0);
          return;
        }
        const { data } =
          await getAcquisitionSourceMediumApiV1AcquisitionSourceMediumGet({
            query: {
              ...getBaseQuery(customerUuid),
              page: sourcesPage,
              page_size: pageSize,
              ...getTableQueryParams(sourcesQuery),
              ...getExtraParamsExcluding("filter_source_medium"),
            },
            throwOnError: true,
          });
        setSourcesData(toTableRows(data?.items || []));
        setSourcesTotal(data?.pagination?.total || 0);
        setSourcesTotalRow(data?.total_row || null);
      } catch (err) {
        console.error(
          "Erro ao carregar Origem/Mídia (acquisition/source-medium):",
          err,
        );
        setSourcesData([]);
        setSourcesTotal(0);
        setSourcesTotalRow(null);
      } finally {
        setSourcesLoading(false);
      }
    };

    loadSources();
  }, [
    startDate,
    endDate,
    comparison,
    sourcesPage,
    sourcesQuery,
    resolveCustomerUuid,
    getBaseQuery,
    getExtraParamsExcluding,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        setCampaignsLoading(true);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setCampaignsData([]);
          setCampaignsTotal(0);
          return;
        }
        const { data } =
          await getAcquisitionCampaignsApiV1AcquisitionCampaignsGet({
            query: {
              ...getBaseQuery(customerUuid),
              page: campaignsPage,
              page_size: pageSize,
              ...getTableQueryParams(campaignsQuery),
              ...getExtraParamsExcluding("filter_campaign"),
            },
            throwOnError: true,
          });
        setCampaignsData(toTableRows(data?.items || []));
        setCampaignsTotal(data?.pagination?.total || 0);
        setCampaignsTotalRow(data?.total_row || null);
      } catch (err) {
        console.error(
          "Erro ao carregar Campanhas (acquisition/campaigns):",
          err,
        );
        setCampaignsData([]);
        setCampaignsTotal(0);
        setCampaignsTotalRow(null);
      } finally {
        setCampaignsLoading(false);
      }
    };

    loadCampaigns();
  }, [
    startDate,
    endDate,
    comparison,
    campaignsPage,
    campaignsQuery,
    resolveCustomerUuid,
    getBaseQuery,
    getExtraParamsExcluding,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  // <auditoria 27> FAIXA ETÁRIA. A rota é nova e publica `total_row`: o rodapé vem dela,
  // como nas tabelas que os itens 11 a 17 consertaram, e não do `StandardizedTable`.
  useEffect(() => {
    const loadAges = async () => {
      try {
        setAgesLoading(true);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setAgesData([]);
          setAgesTotal(0);
          setAgesTotalRow(null);
          return;
        }
        const { data } = await apiV1AcquisitionAudienceQualityGet({
          query: {
            ...getBaseQuery(customerUuid),
            page: agesPage,
            page_size: pageSize,
            ...getTableQueryParams(agesQuery),
            // A rota não aceita `filter_age` — filtrar a tabela pela própria dimensão a
            // reduziria à linha clicada, e por isso a exclusão é do contrato.
            ...getExtraParamsExcluding("filter_age"),
          },
          throwOnError: true,
        });
        setAgesData(toTableRows(data?.items || []));
        setAgesTotal(data?.pagination?.total || 0);
        setAgesTotalRow(data?.total_row || null);
      } catch (err) {
        console.error(
          "Erro ao carregar Faixa Etária (acquisition/audience-quality):",
          err,
        );
        setAgesData([]);
        setAgesTotal(0);
        setAgesTotalRow(null);
      } finally {
        setAgesLoading(false);
      }
    };

    loadAges();
  }, [
    startDate,
    endDate,
    comparison,
    agesPage,
    agesQuery,
    resolveCustomerUuid,
    getBaseQuery,
    getExtraParamsExcluding,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    const loadNewVsReturning = async () => {
      try {
        setNvrLoading(true);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setNvrData([]);
          setNvrTotal(0);
          return;
        }
        const { data } =
          await getAcquisitionNewVsReturningApiV1AcquisitionNewVsReturningGet({
            query: {
              ...getBaseQuery(customerUuid),
              page: nvrPage,
              page_size: pageSize,
              ...getTableQueryParams(nvrQuery),
              ...getExtraParamsExcluding("filter_new_returning"),
            },
            throwOnError: true,
          });
        setNvrData(toTableRows(data?.items || []));
        setNvrTotal(data?.pagination?.total || 0);
        setNvrTotalRow(data?.total_row || null);
      } catch (err) {
        console.error(
          "Erro ao carregar Novos vs Recorrentes (acquisition/new-vs-returning):",
          err,
        );
        setNvrData([]);
        setNvrTotal(0);
        setNvrTotalRow(null);
      } finally {
        setNvrLoading(false);
      }
    };

    loadNewVsReturning();
  }, [
    startDate,
    endDate,
    comparison,
    nvrPage,
    nvrQuery,
    resolveCustomerUuid,
    getBaseQuery,
    getExtraParamsExcluding,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    const loadGeoNewByRegion = async () => {
      try {
        setNewByRegionLoading(true);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setNewByRegionData([]);
          setNewByRegionTotal(0);
          return;
        }
        const { data } =
          await getAcquisitionGeoUsersNewByRegionApiV1AcquisitionGeoUsersNewByRegionGet(
            {
              query: {
                ...getBaseQuery(customerUuid),
                page: newByRegionPage,
                page_size: pageSize,
                ...getTableQueryParams(newByRegionQuery),
                ...getExtraParamsExcluding("filter_region"),
              },
              throwOnError: true,
            },
          );
        setNewByRegionData(toTableRows(data?.items || []));
        setNewByRegionTotal(data?.pagination?.total || 0);
        setNewByRegionTotalRow(data?.total_row || null);
      } catch (err) {
        console.error("Erro ao carregar Geo New Users by Region:", err);
        setNewByRegionData([]);
        setNewByRegionTotal(0);
        setNewByRegionTotalRow(null);
      } finally {
        setNewByRegionLoading(false);
      }
    };

    loadGeoNewByRegion();
  }, [
    startDate,
    endDate,
    comparison,
    newByRegionPage,
    newByRegionQuery,
    resolveCustomerUuid,
    getBaseQuery,
    getExtraParamsExcluding,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    const loadGeoReturningByRegion = async () => {
      try {
        setRetByRegionLoading(true);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setRetByRegionData([]);
          setRetByRegionTotal(0);
          return;
        }
        const { data } =
          await getAcquisitionGeoUsersReturningByRegionApiV1AcquisitionGeoUsersReturningByRegionGet(
            {
              query: {
                ...getBaseQuery(customerUuid),
                page: retByRegionPage,
                page_size: pageSize,
                ...getTableQueryParams(retByRegionQuery),
                ...getExtraParamsExcluding("filter_region"),
              },
              throwOnError: true,
            },
          );
        setRetByRegionData(toTableRows(data?.items || []));
        setRetByRegionTotal(data?.pagination?.total || 0);
        setRetByRegionTotalRow(data?.total_row || null);
      } catch (err) {
        console.error("Erro ao carregar Geo Returning Users by Region:", err);
        setRetByRegionData([]);
        setRetByRegionTotal(0);
        setRetByRegionTotalRow(null);
      } finally {
        setRetByRegionLoading(false);
      }
    };

    loadGeoReturningByRegion();
  }, [
    startDate,
    endDate,
    comparison,
    retByRegionPage,
    retByRegionQuery,
    resolveCustomerUuid,
    getBaseQuery,
    getExtraParamsExcluding,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    const loadGeoNewByRegionSource = async () => {
      try {
        setNewByRegionSourceLoading(true);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setNewByRegionSourceData([]);
          setNewByRegionSourceTotal(0);
          return;
        }
        const { data } =
          await getAcquisitionGeoUsersNewByRegionSourceApiV1AcquisitionGeoUsersNewByRegionSourceGet(
            {
              query: {
                ...getBaseQuery(customerUuid),
                page: newByRegionSourcePage,
                page_size: pageSize,
                ...getTableQueryParams(newByRegionSourceQuery),
                ...getExtraParamsExcluding("filter_region"),
              },
              throwOnError: true,
            },
          );
        setNewByRegionSourceData(toTableRows(data?.items || []));
        setNewByRegionSourceTotal(data?.pagination?.total || 0);
        setNewByRegionSourceTotalRow(data?.total_row || null);
      } catch (err) {
        console.error("Erro ao carregar Geo New Users by Region/Source:", err);
        setNewByRegionSourceData([]);
        setNewByRegionSourceTotal(0);
        setNewByRegionSourceTotalRow(null);
      } finally {
        setNewByRegionSourceLoading(false);
      }
    };

    loadGeoNewByRegionSource();
  }, [
    startDate,
    endDate,
    comparison,
    newByRegionSourcePage,
    newByRegionSourceQuery,
    resolveCustomerUuid,
    getBaseQuery,
    getExtraParamsExcluding,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    const loadGeoReturningByRegionSource = async () => {
      try {
        setRetByRegionSourceLoading(true);
        const customerUuid = resolveCustomerUuid();
        if (!customerUuid) {
          setRetByRegionSourceData([]);
          setRetByRegionSourceTotal(0);
          return;
        }
        const { data } =
          await getAcquisitionGeoUsersReturningByRegionSourceApiV1AcquisitionGeoUsersReturningByRegionSourceGet(
            {
              query: {
                ...getBaseQuery(customerUuid),
                page: retByRegionSourcePage,
                page_size: pageSize,
                ...getTableQueryParams(retByRegionSourceQuery),
                ...getExtraParamsExcluding("filter_region"),
              },
              throwOnError: true,
            },
          );
        setRetByRegionSourceData(toTableRows(data?.items || []));
        setRetByRegionSourceTotal(data?.pagination?.total || 0);
        setRetByRegionSourceTotalRow(data?.total_row || null);
      } catch (err) {
        console.error(
          "Erro ao carregar Geo Returning Users by Region/Source:",
          err,
        );
        setRetByRegionSourceData([]);
        setRetByRegionSourceTotal(0);
        setRetByRegionSourceTotalRow(null);
      } finally {
        setRetByRegionSourceLoading(false);
      }
    };

    loadGeoReturningByRegionSource();
  }, [
    startDate,
    endDate,
    comparison,
    retByRegionSourcePage,
    retByRegionSourceQuery,
    resolveCustomerUuid,
    getBaseQuery,
    getExtraParamsExcluding,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  // Total Geral sempre vindo da API (conjunto completo filtrado)
  const sourcesComputedTotal = useMemo(
    () => sourcesTotalRow || null,
    [sourcesTotalRow],
  );
  const campaignsComputedTotal = useMemo(
    () => campaignsTotalRow || null,
    [campaignsTotalRow],
  );
  const nvrComputedTotal = useMemo(() => nvrTotalRow || null, [nvrTotalRow]);
  const agesComputedTotal = useMemo(() => agesTotalRow || null, [agesTotalRow]);
  const newByRegionComputedTotal = useMemo(
    () => newByRegionTotalRow || null,
    [newByRegionTotalRow],
  );
  const retByRegionComputedTotal = useMemo(
    () => retByRegionTotalRow || null,
    [retByRegionTotalRow],
  );
  const newByRegionSourceComputedTotal = useMemo(
    () => newByRegionSourceTotalRow || null,
    [newByRegionSourceTotalRow],
  );
  const retByRegionSourceComputedTotal = useMemo(
    () => retByRegionSourceTotalRow || null,
    [retByRegionSourceTotalRow],
  );

  // === Cross-filter: handlers para clique nas tabelas ===
  const handleTableRowClick = useCallback(
    (dimension: DrillDimension, valueKey: string) => {
      return (row: TableRow, _index: number, event?: React.MouseEvent) => {
        if (!drilldown) return;
        const val = String(row[valueKey] || "");
        if (!val || val === "N/D") return;
        const isMultiSelect = event?.ctrlKey || event?.metaKey;
        drilldown.selectRow(dimension, val, val, isMultiSelect);
      };
    },
    [drilldown],
  );

  const isTableRowSelected = useCallback(
    (dimension: DrillDimension, valueKey: string) => {
      return (row: TableRow, _index: number): boolean => {
        if (!drilldown) return false;
        return drilldown.isSelected(dimension, String(row[valueKey] || ""));
      };
    },
    [drilldown],
  );

  return (
    <div className="p-6 space-y-6 page-background">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Aquisição</h1>
        <p className="text-muted-foreground">
          Análise detalhada das métricas de aquisição e retenção de usuários
        </p>
      </div>

      <GlobalFilter
        rightSlot={
          <CampaignFiltersControl
            customerUuid={customerUuid}
            accountId={accountId}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* Filtros Ativos de Cross-Filtering */}
      <ActiveFiltersDisplay />

      {/* PRIMEIRA LINHA: CARDS DE RESUMO */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {/* Receita Total */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span>RECEITA TOTAL</span>
                <HelpTooltip
                  content={getKpiTooltip("Receita Total")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    {kpis && Number(kpis.total_revenue || 0) > 0 ? (
                      <KpiNumber
                        value={Number(kpis.total_revenue || 0)}
                        format={(n) => formatCurrency(n)}
                        className="font-light text-foreground"
                        maxFontPx={28}
                        minFontPx={16}
                        breathingRoomPx={16}
                        ariaLabel="Receita total"
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-2xl font-light text-muted-foreground"
                      />
                    )}
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.total_revenue_change_percentage === "number" ? (
                      <TrendIndicator
                        value={kpis.total_revenue_change_percentage}
                        trend={toTrendDirection(
                          kpis.total_revenue_trend_indicator,
                        )}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pedidos */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span>PEDIDOS</span>
                <HelpTooltip
                  content={getKpiTooltip("Pedidos")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    {kpis && Number(kpis.orders || 0) > 0 ? (
                      <KpiNumber
                        value={Number(kpis.orders || 0)}
                        format={(n) => new Intl.NumberFormat("pt-BR").format(n)}
                        className="font-light text-foreground"
                        maxFontPx={28}
                        minFontPx={16}
                        breathingRoomPx={16}
                        ariaLabel="Pedidos"
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-2xl font-light text-muted-foreground"
                      />
                    )}
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.orders_change_percentage === "number" ? (
                      <TrendIndicator
                        value={kpis.orders_change_percentage}
                        trend={toTrendDirection(kpis.orders_trend_indicator)}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Ticket Médio */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span>TICKET MÉDIO</span>
                <HelpTooltip
                  content={getKpiTooltip("Ticket Médio")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    {kpis && Number(kpis.average_order_value || 0) > 0 ? (
                      <KpiNumber
                        value={Number(kpis.average_order_value || 0)}
                        format={(n) => formatCurrency(n)}
                        className="font-light text-foreground"
                        maxFontPx={28}
                        minFontPx={16}
                        breathingRoomPx={16}
                        ariaLabel="Ticket médio"
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-2xl font-light text-muted-foreground"
                      />
                    )}
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.average_order_value_change_percentage ===
                      "number" ? (
                      <TrendIndicator
                        value={kpis.average_order_value_change_percentage}
                        trend={toTrendDirection(
                          kpis.average_order_value_trend_indicator,
                        )}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Taxa Conversão */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span>TAXA CONVERSÃO</span>
                <HelpTooltip
                  content={getKpiTooltip("Taxa Conversão")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    {kpis &&
                    typeof kpis.conversion_rate === "number" &&
                    kpis.conversion_rate > 0 ? (
                      <KpiNumber
                        value={Number(kpis.conversion_rate)}
                        format={(n) => `${n.toFixed(2)}%`}
                        className="font-light text-foreground"
                        maxFontPx={28}
                        minFontPx={16}
                        breathingRoomPx={16}
                        ariaLabel="Taxa de conversão"
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-2xl font-light text-muted-foreground"
                      />
                    )}
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.conversion_rate_change_percentage ===
                      "number" ? (
                      <TrendIndicator
                        value={kpis.conversion_rate_change_percentage}
                        trend={toTrendDirection(
                          kpis.conversion_rate_trend_indicator,
                        )}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SEGUNDA LINHA: CARDS DE RESUMO */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {/* Total de Usuários */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span>TOTAL USUÁRIOS</span>
                <HelpTooltip
                  content={getKpiTooltip("Usuários")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    {kpis && Number(kpis.total_users || 0) > 0 ? (
                      <KpiNumber
                        value={Number(kpis.total_users || 0)}
                        format={(n) => new Intl.NumberFormat("pt-BR").format(n)}
                        className="font-light text-foreground"
                        maxFontPx={28}
                        minFontPx={16}
                        breathingRoomPx={16}
                        ariaLabel="Total de usuários"
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-2xl font-light text-muted-foreground"
                      />
                    )}
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.total_users_change_percentage === "number" ? (
                      <TrendIndicator
                        value={kpis.total_users_change_percentage}
                        trend={toTrendDirection(
                          kpis.total_users_trend_indicator,
                        )}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Sessões */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span>SESSÕES</span>
                <HelpTooltip
                  content={getKpiTooltip("Sessões")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    {kpis && Number(kpis.sessions || 0) > 0 ? (
                      <KpiNumber
                        value={Number(kpis.sessions || 0)}
                        format={(n) => new Intl.NumberFormat("pt-BR").format(n)}
                        className="font-light text-foreground"
                        maxFontPx={28}
                        minFontPx={16}
                        breathingRoomPx={16}
                        ariaLabel="Sessões"
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-2xl font-light text-muted-foreground"
                      />
                    )}
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.sessions_change_percentage === "number" ? (
                      <TrendIndicator
                        value={kpis.sessions_change_percentage}
                        trend={toTrendDirection(kpis.sessions_trend_indicator)}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Sessões Engajadas */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span>SESSÕES ENGAJADAS</span>
                <HelpTooltip
                  content={getKpiTooltip("Sessões Engajadas")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    {kpis && Number(kpis.engaged_sessions || 0) > 0 ? (
                      <KpiNumber
                        value={Number(kpis.engaged_sessions || 0)}
                        format={(n) => new Intl.NumberFormat("pt-BR").format(n)}
                        className="font-light text-foreground"
                        maxFontPx={28}
                        minFontPx={16}
                        breathingRoomPx={16}
                        ariaLabel="Sessões engajadas"
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-2xl font-light text-muted-foreground"
                      />
                    )}
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.engaged_sessions_change_percentage ===
                      "number" ? (
                      <TrendIndicator
                        value={kpis.engaged_sessions_change_percentage}
                        trend={toTrendDirection(
                          kpis.engaged_sessions_trend_indicator,
                        )}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Taxa de Engajamento */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span>TX ENGAJAMENTO</span>
                <HelpTooltip
                  content={getKpiTooltip("Taxa Engajamento")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingKpis ? (
              <CarregandoValor className="text-2xl" />
            ) : (
              <>
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    {kpis &&
                    typeof kpis.engagement_rate === "number" &&
                    kpis.engagement_rate > 0 ? (
                      <KpiNumber
                        value={Number(kpis.engagement_rate)}
                        format={(n) => `${n.toFixed(2)}%`}
                        className="font-light text-foreground"
                        maxFontPx={28}
                        minFontPx={16}
                        breathingRoomPx={16}
                        ariaLabel="Tx Engajamento"
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-2xl font-light text-muted-foreground"
                      />
                    )}
                  </div>
                  {comparisonEnabled &&
                    (kpis &&
                    typeof kpis.engagement_rate_change_percentage ===
                      "number" ? (
                      <TrendIndicator
                        value={kpis.engagement_rate_change_percentage}
                        trend={toTrendDirection(
                          kpis.engagement_rate_trend_indicator,
                        )}
                      />
                    ) : (
                      <ValorIndisponivel
                        fontes={FONTES_DOS_CARDS}
                        erro={erroKpis}
                        className="text-xs text-muted-foreground"
                      />
                    ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SEGUNDA LINHA: GRÁFICO COMPOSTO (Barras + Linhas, 2 eixos Y) */}
      <Card className="card-kpi-secondary">
        <CardContent className="pt-6">
          {/* Legenda no topo */}
          <div className="flex flex-wrap items-center gap-6 mb-6 justify-center">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: "#F59E0B" }}
              ></div>
              <span className="text-sm text-muted-foreground">Pedidos</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: "rgba(250, 204, 21, 0.4)" }}
              ></div>
              <span className="text-sm text-muted-foreground">
                Receita total
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-6 h-0.5"
                style={{ backgroundColor: "#EAB308" }}
              ></div>
              <span className="text-sm text-muted-foreground">
                Investimento (Meta + Google)
              </span>
            </div>
          </div>

          <ScrollableChart minWidth="500px">
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart
                data={timeline.map((p) => {
                  const dataFormatada = p.date_label
                    ? formatDateBR(p.date_label)
                    : "N/D";
                  return {
                    data: dataFormatada,
                    receitaTotal: Number(p.total_revenue || 0),
                    pedidos: Number(p.orders || 0),
                    investimento: Number(p.total_investment || 0),
                  };
                })}
                margin={{ top: 10, right: 50, left: 10, bottom: 0 }}
                barCategoryGap="15%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(0,0,0,0.06)"
                />
                <XAxis
                  dataKey="data"
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                {/* Eixo Y Esquerdo: Pedidos e Investimento (escala menor) */}
                <YAxis
                  yAxisId="left"
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, "auto"]}
                  tickFormatter={(v: number) =>
                    v >= 1000
                      ? `${(v / 1000).toLocaleString("pt-BR")}k`
                      : String(v)
                  }
                />
                {/* Eixo Y Direito: Receita Total (escala maior) */}
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: "#d97706", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, "auto"]}
                  tickFormatter={(v: number) =>
                    v >= 1000
                      ? `${(v / 1000).toLocaleString("pt-BR")}k`
                      : String(v)
                  }
                />
                <RechartsTooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const LABELS: Record<string, string> = {
                      receitaTotal: "Receita Total",
                      pedidos: "Pedidos",
                      investimento: "Investimento (Meta + Google)",
                    };
                    const COLORS: Record<string, string> = {
                      receitaTotal: "rgba(250, 204, 21, 0.7)",
                      pedidos: "#F59E0B",
                      investimento: "#EAB308",
                    };
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                        <p className="text-gray-500 text-xs mb-1">{label}</p>
                        <table style={{ borderSpacing: "8px 2px" }}>
                          <tbody>
                            {payload.map((entry) => {
                              const key = entry.dataKey as string;
                              const formatted =
                                key === "pedidos"
                                  ? Number(entry.value).toLocaleString("pt-BR")
                                  : formatCurrency(Number(entry.value));
                              return (
                                <tr key={key}>
                                  <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full"
                                      style={{
                                        backgroundColor:
                                          COLORS[key] || entry.color,
                                      }}
                                    />
                                    {LABELS[key] || key}:
                                  </td>
                                  <td className="text-right font-semibold text-gray-900 pl-3">
                                    {formatted}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  }}
                />
                {/* Barras: Receita Total no eixo direito */}
                <Bar
                  yAxisId="right"
                  dataKey="receitaTotal"
                  fill="rgba(250, 204, 21, 0.4)"
                  radius={[3, 3, 0, 0]}
                  name="receitaTotal"
                />
                {/* Linha: Pedidos com bolinhas no eixo esquerdo */}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="pedidos"
                  stroke="#F59E0B"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#F59E0B", strokeWidth: 0 }}
                  name="pedidos"
                  label={({ x, y, value }: LineLabelProps) => {
                    const v = Number(value) || 0;
                    const yPosition = Number(y);
                    if (v === 0 || !Number.isFinite(yPosition)) return <g />;
                    return (
                      <text
                        x={x}
                        y={yPosition - 10}
                        textAnchor="middle"
                        fill="#92400e"
                        fontSize={10}
                        fontWeight={600}
                      >
                        {v.toLocaleString("pt-BR")}
                      </text>
                    );
                  }}
                />
                {/* Linha: Investimento (Meta + Google) reta no eixo esquerdo */}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="investimento"
                  stroke="#EAB308"
                  strokeWidth={2}
                  dot={false}
                  name="investimento"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ScrollableChart>
        </CardContent>
      </Card>

      {/* TABELA COM DRILL-DOWN - OCULTA conforme solicitação */}
      {/* <AcquisitionDrilldownSection /> */}

      {/* TERCEIRA LINHA: AGRUPAMENTO DE CANAL PADRÃO */}
      <StandardizedTable
        title="AGRUPAMENTO DE CANAL PADRÃO"
        description="Análise de performance por canal padrão"
        loading={channelGroupingLoading}
        columns={[
          {
            key: "channel_group",
            header: "Canal padrão",
            align: "left",
            render: (value, item, index, isTotal) =>
              isTotal
                ? value
                : `${(channelGroupingPage - 1) * pageSize + index + 1}. ${value}`,
          },
          {
            key: "sessions",
            header: "Sessões",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "sessions_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "carts",
            header: "Carrinhos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "carts_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "orders",
            header: "Pedidos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "orders_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "avg_order_value",
            header: "TM",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "avg_order_value_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "conversion_rate",
            header: "TX Conversão",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "conversion_rate_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "total_revenue",
            header: "Receita total",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "total_revenue_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "total_users",
            header: "Usuários",
            align: "right",
            visible: false,
            render: (value) => formatNumber(Number(value || 0)),
          },
          {
            key: "total_users_delta",
            header: "% Δ",
            align: "right",
            visible: false,
            render: renderTableDelta,
          },
          {
            key: "new_users",
            header: "Novos usuários",
            align: "right",
            visible: false,
            render: (value) => {
              const v = Number(value || 0);
              return v > 0 ? formatNumber(v) : "N/D";
            },
          },
          {
            key: "new_users_delta",
            header: "% Δ",
            align: "right",
            visible: false,
            render: renderTableDelta,
          },
          // <auditoria 27> As duas colunas de "Qualidade do Público" nascem **visíveis**.
          // Elas já eram declaradas aqui, escondidas atrás do seletor de colunas, e as duas
          // saíam `N/D` em toda linha: `sessions_per_user` não existia em rota nenhuma do
          // projeto, e a tabela CAMPANHAS não publicava `engaged_sessions`. Consertadas as
          // rotas, escondê-las manteria de pé a crítica do item 27 — a página do Looker
          // mostra as duas, e quem lê a tela não abre o seletor para descobrir que existem.
          {
            key: "engaged_sessions",
            header: "Sessões Eng.",
            align: "right",
            render: (value) => {
              const v = Number(value || 0);
              return v > 0 ? formatNumber(v) : "N/D";
            },
          },
          {
            key: "engaged_sessions_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "sessions_per_user",
            header: "Sessões P/ Usuário",
            align: "right",
            render: (value) => {
              const v = Number(value || 0);
              return v > 0
                ? v.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "N/D";
            },
          },
          {
            key: "sessions_per_user_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
        ]}
        data={channelGroupingData}
        total={channelGroupingTotal ?? undefined}
        pagination={{
          current: channelGroupingPage,
          pageSize,
          total: channelGroupingTotalCount,
          onPageChange: (p) => setChannelGroupingPage(p),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setChannelGroupingQuery(q);
          setChannelGroupingPage(1);
        }}
        onRowClick={
          drilldown
            ? handleTableRowClick("channel_group", "channel_group")
            : undefined
        }
        selectedRowIndex={
          drilldown
            ? isTableRowSelected("channel_group", "channel_group")
            : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* QUARTA LINHA: ORIGEM / MÍDIA */}
      <StandardizedTable
        title="ORIGEM / MÍDIA"
        description="Análise de performance por origem e mídia da sessão"
        columns={[
          {
            key: "source_medium",
            header: "Origem / Mídia",
            align: "left",
            render: (value, item, index, isTotal) =>
              isTotal
                ? value
                : `${(sourcesPage - 1) * pageSize + index + 1}. ${value}`,
          },
          {
            key: "sessions",
            header: "Sessões",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "sessions_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "carts",
            header: "Carrinhos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "carts_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "orders",
            header: "Pedidos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "orders_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "avg_ticket",
            header: "TM",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "average_order_value_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "conversion_rate",
            header: "TX Conversão",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "conversion_rate_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "revenue",
            header: "Receita total",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "revenue_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "total_users",
            header: "Usuários",
            align: "right",
            visible: false,
            render: (value) => formatNumber(Number(value || 0)),
          },
          {
            key: "total_users_delta",
            header: "% Δ",
            align: "right",
            visible: false,
            render: renderTableDelta,
          },
          {
            key: "new_users",
            header: "Novos usuários",
            align: "right",
            visible: false,
            render: (value) => {
              const v = Number(value || 0);
              return v > 0 ? formatNumber(v) : "N/D";
            },
          },
          {
            key: "new_users_delta",
            header: "% Δ",
            align: "right",
            visible: false,
            render: renderTableDelta,
          },
          // <auditoria 27> As duas colunas de "Qualidade do Público" nascem **visíveis**.
          // Elas já eram declaradas aqui, escondidas atrás do seletor de colunas, e as duas
          // saíam `N/D` em toda linha: `sessions_per_user` não existia em rota nenhuma do
          // projeto, e a tabela CAMPANHAS não publicava `engaged_sessions`. Consertadas as
          // rotas, escondê-las manteria de pé a crítica do item 27 — a página do Looker
          // mostra as duas, e quem lê a tela não abre o seletor para descobrir que existem.
          {
            key: "engaged_sessions",
            header: "Sessões Eng.",
            align: "right",
            render: (value) => {
              const v = Number(value || 0);
              return v > 0 ? formatNumber(v) : "N/D";
            },
          },
          {
            key: "engaged_sessions_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "sessions_per_user",
            header: "Sessões P/ Usuário",
            align: "right",
            render: (value) => {
              const v = Number(value || 0);
              return v > 0
                ? v.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "N/D";
            },
          },
          {
            key: "sessions_per_user_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
        ]}
        data={sourcesData}
        loading={sourcesLoading}
        total={sourcesComputedTotal ?? undefined}
        pagination={{
          current: sourcesPage,
          pageSize: pageSize,
          total: sourcesTotal,
          onPageChange: (p) => setSourcesPage(p),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setSourcesQuery(q);
          setSourcesPage(1);
        }}
        onRowClick={
          drilldown
            ? handleTableRowClick("source_medium", "source_medium")
            : undefined
        }
        selectedRowIndex={
          drilldown
            ? isTableRowSelected("source_medium", "source_medium")
            : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* QUINTA LINHA: CAMPANHAS */}
      <StandardizedTable
        title="CAMPANHAS"
        description="Campanha da sessão"
        columns={[
          {
            key: "campaign",
            header: "Campanha da sessão",
            align: "left",
            render: (value, item, index, isTotal) =>
              isTotal
                ? value
                : `${(campaignsPage - 1) * pageSize + index + 1}. ${value}`,
          },
          {
            key: "sessions",
            header: "Sessões",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "sessions_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "carts",
            header: "Carrinhos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "carts_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "orders",
            header: "Pedidos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "orders_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "avg_ticket",
            header: "TM",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "average_order_value_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "conversion_rate",
            header: "TX Conversão",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "conversion_rate_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "revenue",
            header: "Receita total",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "revenue_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "total_users",
            header: "Usuários",
            align: "right",
            visible: false,
            render: (value) => formatNumber(Number(value || 0)),
          },
          {
            key: "total_users_delta",
            header: "% Δ",
            align: "right",
            visible: false,
            render: renderTableDelta,
          },
          {
            key: "new_users",
            header: "Novos usuários",
            align: "right",
            visible: false,
            render: (value) => {
              const v = Number(value || 0);
              return v > 0 ? formatNumber(v) : "N/D";
            },
          },
          {
            key: "new_users_delta",
            header: "% Δ",
            align: "right",
            visible: false,
            render: renderTableDelta,
          },
          // <auditoria 27> As duas colunas de "Qualidade do Público" nascem **visíveis**.
          // Elas já eram declaradas aqui, escondidas atrás do seletor de colunas, e as duas
          // saíam `N/D` em toda linha: `sessions_per_user` não existia em rota nenhuma do
          // projeto, e a tabela CAMPANHAS não publicava `engaged_sessions`. Consertadas as
          // rotas, escondê-las manteria de pé a crítica do item 27 — a página do Looker
          // mostra as duas, e quem lê a tela não abre o seletor para descobrir que existem.
          {
            key: "engaged_sessions",
            header: "Sessões Eng.",
            align: "right",
            render: (value) => {
              const v = Number(value || 0);
              return v > 0 ? formatNumber(v) : "N/D";
            },
          },
          {
            key: "engaged_sessions_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "sessions_per_user",
            header: "Sessões P/ Usuário",
            align: "right",
            render: (value) => {
              const v = Number(value || 0);
              return v > 0
                ? v.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "N/D";
            },
          },
          {
            key: "sessions_per_user_delta",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
        ]}
        data={campaignsData}
        loading={campaignsLoading}
        total={campaignsComputedTotal ?? undefined}
        pagination={{
          current: campaignsPage,
          pageSize: pageSize,
          total: campaignsTotal,
          onPageChange: (p) => setCampaignsPage(p),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setCampaignsQuery(q);
          setCampaignsPage(1);
        }}
        onRowClick={
          drilldown
            ? handleTableRowClick("campaign" as DrillDimension, "campaign")
            : undefined
        }
        selectedRowIndex={
          drilldown
            ? isTableRowSelected("campaign" as DrillDimension, "campaign")
            : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* SEXTA LINHA: NOVOS X RECORRENTES */}
      <StandardizedTable
        title="NOVOS X RECORRENTES"
        description="Análise de usuários novos versus recorrentes"
        columns={[
          {
            key: "new_vs_returning",
            header: "Novo / recorrente",
            align: "left",
            render: (value, item, _index, isTotal) =>
              isTotal
                ? value
                : value === "returning" || value === "new"
                  ? value
                  : (item as TableRow)?.new_users > 0
                    ? "new"
                    : "returning",
          },
          {
            key: "total_users",
            header: "Total de usuários",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "new_users",
            header: "Novos usuários",
            align: "right",
            render: (value) => (value > 0 ? formatNumber(value) : "-"),
          },
          {
            key: "sessions",
            header: "Sessões",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "sessions_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "conversion_rate",
            header: "TX Conversão",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "conversion_rate_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "orders",
            header: "Pedidos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "orders_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "avg_ticket",
            header: "TM",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "average_order_value_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "revenue",
            header: "Receita total",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "revenue_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
        ]}
        data={nvrData}
        loading={nvrLoading}
        total={nvrComputedTotal ?? undefined}
        pagination={{
          current: nvrPage,
          pageSize: pageSize,
          total: nvrTotal,
          onPageChange: (p) => setNvrPage(p),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setNvrQuery(q);
          setNvrPage(1);
        }}
        onRowClick={
          drilldown
            ? handleTableRowClick("new_returning", "new_vs_returning")
            : undefined
        }
        selectedRowIndex={
          drilldown
            ? isTableRowSelected("new_returning", "new_vs_returning")
            : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* <auditoria 27> QUALIDADE DO PÚBLICO POR FAIXA ETÁRIA.

          A página "Qualidade do Público" do Looker mostra sessões por usuário, sessões
          engajadas e um recorte por idade; o painel não tinha nenhum dos três. As duas
          primeiras viraram colunas visíveis nas tabelas acima, e esta é a terceira — a
          única tabela desta página cuja rota nasceu publicando `total_row`, para que o
          rodapé não some razão nenhuma. */}
      <StandardizedTable
        title="QUALIDADE DO PÚBLICO POR FAIXA ETÁRIA"
        description="Engajamento e conversão por faixa etária do usuário"
        columns={[
          {
            key: "age",
            header: "Faixa etária",
            align: "left",
            render: (value, item, index, isTotal) =>
              isTotal
                ? value
                : `${(agesPage - 1) * pageSize + index + 1}. ${value}`,
          },
          {
            key: "sessions",
            header: "Sessões",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "sessions_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "total_users",
            header: "Usuários",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "sessions_per_user",
            header: "Sessões P/ Usuário",
            align: "right",
            render: (value) => {
              const v = Number(value || 0);
              return v > 0
                ? v.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "N/D";
            },
          },
          {
            key: "engaged_sessions",
            header: "Sessões Eng.",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "engagement_rate",
            header: "TX Engajamento",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "average_engagement_time",
            header: "Tempo Médio Eng.",
            align: "right",
            render: (value) => {
              const v = Number(value || 0);
              return v > 0
                ? `${v.toLocaleString("pt-BR", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}s`
                : "N/D";
            },
          },
          {
            key: "orders",
            header: "Pedidos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "conversion_rate",
            header: "TX Conversão",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "revenue",
            header: "Receita total",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "revenue_change_percentage",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
        ]}
        data={agesData}
        loading={agesLoading}
        total={agesComputedTotal ?? undefined}
        pagination={{
          current: agesPage,
          pageSize: pageSize,
          total: agesTotal,
          onPageChange: (p) => setAgesPage(p),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setAgesQuery(q);
          setAgesPage(1);
        }}
      />

      {/* SÉTIMA LINHA: NOVOS USUÁRIOS POR REGIÃO */}
      <StandardizedTable
        title="NOVOS USUÁRIOS POR REGIÃO"
        description="Análise de novos usuários por região geográfica"
        columns={[
          {
            key: "regiao",
            header: "Região",
            align: "left",
          },
          {
            key: "novosUsuarios",
            header: "Novos usuários",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "novosUsuariosChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "txConversao",
            header: "TX Conversão",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "txConversaoChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "pedidos",
            header: "Pedidos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "pedidosChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "tm",
            header: "TM",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "tmChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "receitaTotal",
            header: "Receita total",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "receitaChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
        ]}
        data={newByRegionData}
        loading={newByRegionLoading}
        total={newByRegionComputedTotal ?? undefined}
        pagination={{
          current: newByRegionPage,
          pageSize: pageSize,
          total: newByRegionTotal,
          onPageChange: (p) => setNewByRegionPage(p),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setNewByRegionQuery(q);
          setNewByRegionPage(1);
        }}
        onRowClick={
          drilldown ? handleTableRowClick("region", "regiao") : undefined
        }
        selectedRowIndex={
          drilldown ? isTableRowSelected("region", "regiao") : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* OITAVA LINHA: USUÁRIOS QUE RETORNAM POR REGIÃO */}
      <StandardizedTable
        title="USUÁRIOS QUE RETORNAM POR REGIÃO"
        description="Análise de usuários recorrentes por região geográfica"
        columns={[
          {
            key: "regiao",
            header: "Região",
            align: "left",
          },
          {
            key: "usuariosRetornam",
            header: "Total de usuários",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "usuariosRetornamChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "txConversao",
            header: "TX Conversão",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "txConversaoChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "pedidos",
            header: "Pedidos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "pedidosChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "tm",
            header: "TM",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "tmChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "receitaTotal",
            header: "Receita total",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "receitaChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
        ]}
        data={retByRegionData}
        loading={retByRegionLoading}
        total={retByRegionComputedTotal ?? undefined}
        pagination={{
          current: retByRegionPage,
          pageSize: pageSize,
          total: retByRegionTotal,
          onPageChange: (p) => setRetByRegionPage(p),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setRetByRegionQuery(q);
          setRetByRegionPage(1);
        }}
        onRowClick={
          drilldown ? handleTableRowClick("region", "regiao") : undefined
        }
        selectedRowIndex={
          drilldown ? isTableRowSelected("region", "regiao") : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* NONA LINHA: NOVOS USUÁRIOS POR REGIÃO E ORIGEM/MÍDIA DA SESSÃO */}
      <StandardizedTable
        title="NOVOS USUÁRIOS POR REGIÃO E ORIGEM / MÍDIA DA SESSÃO"
        description="Análise detalhada de novos usuários por região e origem/mídia"
        columns={[
          {
            key: "regiao",
            header: "Região",
            align: "left",
          },
          {
            key: "origem",
            header: "Origem / Mídia",
            align: "left",
          },
          {
            key: "novosUsuarios",
            header: "Total de usuários",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "novosUsuariosChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "txConversao",
            header: "TX Conversão",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "txConversaoChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "pedidos",
            header: "Pedidos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "pedidosChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "tm",
            header: "TM",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "tmChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "receitaTotal",
            header: "Receita total",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "receitaChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
        ]}
        data={newByRegionSourceData}
        loading={newByRegionSourceLoading}
        total={newByRegionSourceComputedTotal ?? undefined}
        pagination={{
          current: newByRegionSourcePage,
          pageSize: pageSize,
          total: newByRegionSourceTotal,
          onPageChange: (p) => setNewByRegionSourcePage(p),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setNewByRegionSourceQuery(q);
          setNewByRegionSourcePage(1);
        }}
        onRowClick={
          drilldown ? handleTableRowClick("region", "regiao") : undefined
        }
        selectedRowIndex={
          drilldown ? isTableRowSelected("region", "regiao") : undefined
        }
        clickableRows={!!drilldown}
      />

      {/* DÉCIMA LINHA: USUÁRIOS QUE RETORNAM POR REGIÃO E ORIGEM/MÍDIA DA SESSÃO */}
      <StandardizedTable
        title="USUÁRIOS QUE RETORNAM POR REGIÃO E ORIGEM / MÍDIA DA SESSÃO"
        description="Análise detalhada de usuários recorrentes por região e origem/mídia"
        columns={[
          {
            key: "regiao",
            header: "Região",
            align: "left",
          },
          {
            key: "origem",
            header: "Origem / Mídia",
            align: "left",
          },
          {
            key: "usuariosRetornam",
            header: "Total de usuários",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "usuariosRetornamChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "txConversao",
            header: "TX Conversão",
            align: "right",
            render: (value) => formatPercentage(value),
          },
          {
            key: "txConversaoChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "pedidos",
            header: "Pedidos",
            align: "right",
            render: (value) => formatNumber(value),
          },
          {
            key: "pedidosChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "tm",
            header: "TM",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "tmChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
          {
            key: "receitaTotal",
            header: "Receita total",
            align: "right",
            render: (value) => formatCurrency(value),
          },
          {
            key: "receitaChange",
            header: "% Δ",
            align: "right",
            render: renderTableDelta,
          },
        ]}
        data={retByRegionSourceData}
        loading={retByRegionSourceLoading}
        total={retByRegionSourceComputedTotal ?? undefined}
        pagination={{
          current: retByRegionSourcePage,
          pageSize: pageSize,
          total: retByRegionSourceTotal,
          onPageChange: (p) => setRetByRegionSourcePage(p),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setRetByRegionSourceQuery(q);
          setRetByRegionSourcePage(1);
        }}
        onRowClick={
          drilldown ? handleTableRowClick("region", "regiao") : undefined
        }
        selectedRowIndex={
          drilldown ? isTableRowSelected("region", "regiao") : undefined
        }
        clickableRows={!!drilldown}
      />
    </div>
  );
};

export default AcquisitionPage;
